"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icons } from "@/components/shared/icons";
import { SocialButton } from "@/components/base/buttons/social-button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: string;
  onSuccess?: () => void;
}

type AuthStep = "providers" | "email" | "otp";

const FlyingMailIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="54"
    height="54"
    viewBox="0 0 54 54"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-12 text-foreground"
    {...props}
  >
    {/* Speed trails */}
    <path d="M6 29h-3" />
    <path d="M9 22H3" />
    {/* Envelope Body */}
    <path d="M14 15h26a3 3 0 0 1 3 3v20a3 3 0 0 1-3 3H14a3 3 0 0 1-3-3V18a3 3 0 0 1 3-3Z" />
    <path d="m11 17 16 12 16-12" />
  </svg>
);

export function UserAuthForm({ className, onSuccess, ...props }: UserAuthFormProps) {
  const [step, setStep] = React.useState<AuthStep>("providers");
  const [email, setEmail] = React.useState<string>("");
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [otp, setOtp] = React.useState<string>("");
  const [otpError, setOtpError] = React.useState<string | null>(null);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isGoogleLoading, setIsGoogleLoading] = React.useState<boolean>(false);
  const [isDiscordLoading, setIsDiscordLoading] = React.useState<boolean>(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = React.useState<boolean>(false);

  const [cooldown, setCooldown] = React.useState<number>(0);
  const searchParams = useSearchParams();
  const router = useRouter();

  // Cooldown countdown timer effect for resending code
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const validateEmail = (val: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val || !emailRegex.test(val.trim())) {
      setEmailError("Please enter a valid email address.");
      return false;
    }
    setEmailError(null);
    return true;
  };

  // Helper to determine return URL (current page if in modal/page, or 'from' param)
  const getCallbackUrl = () => {
    return (
      searchParams?.get("from") ||
      (typeof window !== "undefined" ? window.location.href : "/")
    );
  };

  // Google OAuth flow
  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    signIn("google", {
      callbackUrl: getCallbackUrl(),
    });
  };

  // Discord OAuth flow
  const handleDiscordSignIn = () => {
    setIsDiscordLoading(true);
    signIn("discord", {
      callbackUrl: getCallbackUrl(),
    });
  };

  // Single unified email submission via NextAuth Nodemailer provider
  const handleEmailSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateEmail(email)) return;

    setIsLoading(true);
    setOtpError(null);

    try {
      const normalizedEmail = email.toLowerCase().trim();

      const signInResult = await signIn("nodemailer", {
        email: normalizedEmail,
        redirect: false,
        callbackUrl: getCallbackUrl(),
      });

      if (!signInResult?.ok) {
        throw new Error("Failed to send verification code. Please try again.");
      }

      toast.success("Verification code & link sent", {
        description: `We've sent a 6-digit code to ${normalizedEmail}`,
      });

      setOtp("");
      setCooldown(60);
      setStep("otp");
    } catch (err: any) {
      toast.error("Error", {
        description: err.message || "Failed to send verification code.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Verify the 6-digit OTP code with NextAuth Credentials provider
  const handleVerifyOtp = async (codeToVerify?: string) => {
    const code = (codeToVerify || otp).trim();
    if (code.length !== 6) return;

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const result = await signIn("otp", {
        email: email.toLowerCase().trim(),
        otp: code,
        redirect: false,
        callbackUrl: getCallbackUrl(),
      });

      if (!result?.ok || result?.error) {
        setOtpError("Invalid code. Please try again.");
        setIsVerifyingOtp(false);
        return;
      }

      toast.success("Signed in successfully!");
      if (onSuccess) {
        onSuccess();
        router.refresh();
      } else {
        const from = searchParams?.get("from");
        if (from) {
          router.push(from);
        }
        router.refresh();
      }
    } catch (err) {
      setOtpError("Invalid code. Please try again.");
      setIsVerifyingOtp(false);
    }
  };

  return (
    <div className={cn("w-full space-y-4", className)} {...props}>
      {/* 1. Main Provider Selection View */}
      {step === "providers" && (
        <div className="space-y-6">
          <div className="flex flex-col items-center space-y-3 text-center mb-2">
            <div className="flex size-12 items-center justify-center rounded-xl border border-border/80 bg-muted/40 shadow-xs">
              <Icons.logo className="size-6 text-foreground" />
            </div>
            <div className="space-y-1">
              <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl font-urban">
                Welcome back
              </h2>
              <p className="text-sm text-muted-foreground">
                Welcome back! Please enter your details.
              </p>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3">
            <SocialButton
              social="google"
              theme="brand"
              size="lg"
              className="w-full justify-center h-11 text-sm font-medium shadow-xs rounded-xl cursor-pointer"
              onClick={handleGoogleSignIn}
              disabled={isLoading || isGoogleLoading || isDiscordLoading}
            >
              {isGoogleLoading ? "Signing in..." : "Sign in with Google"}
            </SocialButton>

            <SocialButton
              social="discord"
              theme="brand"
              size="lg"
              className="w-full justify-center h-11 text-sm font-medium shadow-xs rounded-xl cursor-pointer"
              onClick={handleDiscordSignIn}
              disabled={isLoading || isGoogleLoading || isDiscordLoading}
            >
              {isDiscordLoading ? "Signing in..." : "Sign in with Discord"}
            </SocialButton>

            <SocialButton
              social="email"
              theme="brand"
              size="lg"
              className="w-full justify-center h-11 text-sm font-medium shadow-xs rounded-xl cursor-pointer"
              onClick={() => setStep("email")}
              disabled={isLoading || isGoogleLoading || isDiscordLoading}
            >
              Sign in with email
            </SocialButton>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  defaultChecked
                  className="size-4 rounded border-border text-primary focus:ring-ring accent-primary cursor-pointer"
                />
                <label
                  htmlFor="remember-me"
                  className="text-xs sm:text-sm text-muted-foreground select-none cursor-pointer font-medium"
                >
                  Remember for 30 days
                </label>
              </div>

              <button
                type="button"
                onClick={() => setStep("email")}
                className="text-xs sm:text-sm font-semibold text-primary hover:underline transition-colors cursor-pointer"
              >
                Get help
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Email Address Input Step - Single 'Continue' Button */}
      {step === "email" && (
        <form onSubmit={handleEmailSubmit} className="space-y-5 w-full">
          <div className="flex flex-col items-center space-y-3 text-center mb-2">
            <div className="flex size-12 items-center justify-center rounded-xl border border-border/80 bg-muted/40 shadow-xs">
              <Icons.logo className="size-6 text-foreground" />
            </div>
            <div className="space-y-1">
              <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl font-urban">
                Welcome back
              </h2>
              <p className="text-sm text-muted-foreground">
                Enter your email to receive your sign-in code and link.
              </p>
            </div>
          </div>

          <div className="space-y-1.5 text-left">
            <Label htmlFor="email" className="text-sm font-medium text-foreground">
              Email
            </Label>
            <Input
              id="email"
              placeholder="name@example.com"
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              className={cn(
                "h-11 rounded-xl border border-border bg-background px-3.5 text-sm",
                emailError && "border-red-500 focus-visible:ring-red-500"
              )}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              disabled={isLoading}
              autoFocus
            />
            {emailError && (
              <p className="px-1 text-xs text-red-500">{emailError}</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full rounded-xl h-11 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow-xs flex items-center justify-center gap-2 text-sm cursor-pointer disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading && <Icons.spinner className="size-4 animate-spin" />}
            Continue
          </button>

          <button
            type="button"
            onClick={() => setStep("providers")}
            className="w-full text-center text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground pt-1 flex items-center justify-center gap-1 transition-colors cursor-pointer"
          >
            ‹ All sign in options
          </button>
        </form>
      )}

      {/* 3. 6-Digit OTP Verification Step (Matching Image 1 Design) */}
      {step === "otp" && (
        <div className="space-y-4 w-full">
          <div className="flex flex-col items-center text-center">
            {/* Flying Mail Icon */}
            <div className="flex items-center justify-center mb-1">
              <FlyingMailIcon />
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif font-normal text-center text-foreground tracking-tight mt-3 mb-2">
              Check your email inbox
            </h2>

            <p className="text-center text-xs sm:text-sm text-muted-foreground leading-relaxed">
              To sign in, click the magic link or enter the code we sent to:
            </p>
            <p className="text-center font-bold text-xs sm:text-sm text-foreground mt-1 mb-2">
              {email}{" "}
              <button
                type="button"
                onClick={() => setStep("email")}
                className="text-xs font-normal text-primary underline ml-1 cursor-pointer"
              >
                (Edit)
              </button>
            </p>
          </div>

          <div className="flex flex-col items-center justify-center py-2">
            <InputOTP
              maxLength={6}
              value={otp}
              onChange={(value) => {
                setOtp(value);
                if (otpError) setOtpError(null);
                if (value.length === 6) {
                  handleVerifyOtp(value);
                }
              }}
              disabled={isVerifyingOtp}
              autoFocus
            >
              <InputOTPGroup className="gap-2 sm:gap-2.5">
                <InputOTPSlot index={0} isError={!!otpError} />
                <InputOTPSlot index={1} isError={!!otpError} />
                <InputOTPSlot index={2} isError={!!otpError} />
                <InputOTPSlot index={3} isError={!!otpError} />
                <InputOTPSlot index={4} isError={!!otpError} />
                <InputOTPSlot index={5} isError={!!otpError} />
              </InputOTPGroup>
            </InputOTP>

            {/* Error indicator under boxes */}
            {otpError && (
              <p className="mt-3 text-center text-sm font-medium text-red-500">
                {otpError}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => handleVerifyOtp(otp)}
            disabled={otp.length !== 6 || isVerifyingOtp || !!otpError}
            className="w-full rounded-xl h-11 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow-xs flex items-center justify-center gap-2 text-sm cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isVerifyingOtp && <Icons.spinner className="size-4 animate-spin" />}
            Verify & Sign In
          </button>

          <div className="flex flex-col items-center justify-center gap-2 pt-2 text-xs">
            {cooldown > 0 ? (
              <span className="text-muted-foreground font-medium">
                Resend code in 0:{cooldown < 10 ? `0${cooldown}` : cooldown}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => handleEmailSubmit()}
                className="text-primary font-semibold hover:underline cursor-pointer"
                disabled={isLoading}
              >
                Didn&apos;t receive code? Resend code
              </button>
            )}

            <button
              type="button"
              onClick={() => setStep("providers")}
              className="font-medium text-muted-foreground hover:text-foreground pt-1 transition-colors cursor-pointer"
            >
              ‹ All sign in options
            </button>
          </div>
        </div>
      )}

      {/* Footer Terms Note */}
      <p className="pt-2 text-center text-[11px] sm:text-xs text-muted-foreground leading-relaxed">
        By signing in, you agree to our{" "}
        <Link
          href="/terms"
          className="underline underline-offset-4 hover:text-foreground transition-colors font-medium"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href="/privacy"
          className="underline underline-offset-4 hover:text-foreground transition-colors font-medium"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
